import { Wrench, ShieldAlert, Download, MessageSquareText, Printer, PackageX, Wifi, HardDrive, PhoneCall, CheckCircle2, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

/*
 * "Fix Your Computer with AI" — a free, naive-user-proof guide.
 * Facts verified Aug 2026 against official Anthropic and Microsoft docs:
 * - Claude Code desktop (Code tab) is the only Anthropic consumer surface that
 *   runs real commands on the host OS, with per-command Accept/Reject approval.
 * - Requires Claude Pro (~$20/mo); free plan has no local-agent capability.
 * - SaRA is retired; Office removal now via Get Help / GetHelpCmd.exe.
 */

const SAFETY_RULES = [
  {
    rule: 'Keep Claude in "Manual" mode.',
    detail: 'It’s the factory setting: Claude proposes, you approve, nothing runs until you click Accept. Never change this setting, and never let anyone talk you into "skip permissions" mode.',
  },
  {
    rule: 'Back up first, fix second.',
    detail: 'Turn on Time Machine (Mac) or File History (Windows) once, and every future mistake becomes recoverable. Ask Claude: "Is my computer backed up? Help me check."',
  },
  {
    rule: 'Watch for the word "delete."',
    detail: 'Before you click Accept, scan the command for rm, del, erase, or format. If you see one and don’t know exactly what it removes, click Reject and type: "Explain what that command would delete."',
  },
  {
    rule: 'Passwords go in system prompts only.',
    detail: 'Sometimes your computer itself will ask for your password to allow a repair — that’s normal. But never type a password into the chat box. No legitimate fix needs that.',
  },
  {
    rule: 'One problem at a time.',
    detail: '"Clean up my whole computer" is how AI helpers cause damage. "Fix the stuck print job on my HP printer" is how they shine. Small, specific asks.',
  },
];

const MORE_ASKS = [
  {
    icon: Wifi,
    title: '“The internet works but websites won’t load”',
    prompt: 'My Wi-Fi says connected but web pages won’t open. Flush the DNS cache and explain what you’re doing.',
    note: 'On a Mac this fix finishes silently — no news is good news. Claude will confirm when it’s done.',
  },
  {
    icon: HardDrive,
    title: '“My Mac says the disk is almost full”',
    prompt: 'My disk is almost full. Find my ten biggest folders and show me the list — don’t delete anything.',
    note: 'Let Claude find the space-hogs; you decide what goes. Diagnosis by AI, deletion by human.',
  },
  {
    icon: Wrench,
    title: '“My computer takes forever to start”',
    prompt: 'My Windows computer starts slowly. Show me what programs launch at startup and which ones are safe to turn off.',
    note: 'Claude explains each one in plain English before you decide.',
  },
];

export default function FixIt() {
  return (
    <div className="max-w-3xl mx-auto">
      {/* Hero */}
      <div className="text-center mb-12">
        <span className="inline-flex items-center gap-2 bg-gray-800 text-accent-orange px-4 py-1.5 rounded-full text-sm font-medium mb-6">
          <Wrench className="w-4 h-4" aria-hidden="true" /> Free guide &mdash; no account needed to read it
        </span>
        <h1 className="text-4xl font-bold text-white mb-4">Fix Your Computer with AI</h1>
        <p className="text-lg text-gray-300">
          The stuck printer. The program that won&rsquo;t uninstall. The internet that&rsquo;s
          &ldquo;connected&rdquo; but broken. An AI assistant can now fix these for you &mdash; on your
          actual computer, in plain English, while asking your permission at every step. Here&rsquo;s
          exactly how, with no steps skipped.
        </p>
      </div>

      {/* Honesty box */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-12">
        <h2 className="text-lg font-semibold text-white mb-3">The honest fine print, up front</h2>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 shrink-0" aria-hidden="true" /><span>This guide uses <strong>Claude</strong> (from Anthropic). The plan that can actually fix your computer is <strong>Claude Pro, about $20/month</strong> &mdash; the free plan can chat but can&rsquo;t run repairs. We&rsquo;d rather tell you that now than surprise you at step 4.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 shrink-0" aria-hidden="true" /><span>Want to try before paying? <strong>Text us at (216)&nbsp;999-5765</strong> and we&rsquo;ll send you a free one-week guest pass while we have them to give.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 shrink-0" aria-hidden="true" /><span>Your computer needs to be reasonably current: <strong>macOS 13 or newer</strong>, or <strong>Windows 10/11</strong>. Older machines: skip to the &ldquo;rather have a human?&rdquo; option at the bottom.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 shrink-0" aria-hidden="true" /><span>We don&rsquo;t get paid by Anthropic. We teach this because it works and it&rsquo;s the same tool we build with every day.</span></li>
        </ul>
      </div>

      {/* Safety rules — deliberately before installation */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-accent-orange" aria-hidden="true" /> Five rules before you start
        </h2>
        <p className="text-gray-400 mb-6 text-sm">
          Read these first. They&rsquo;re the difference between &ldquo;my printer works again&rdquo; and a bad afternoon.
        </p>
        <ol className="space-y-4">
          {SAFETY_RULES.map((item, i) => (
            <li key={i} className="bg-gray-800/30 rounded-xl p-4">
              <p className="font-semibold text-white">{i + 1}. {item.rule}</p>
              <p className="text-gray-300 text-sm mt-1">{item.detail}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Step 1: install */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Download className="w-6 h-6 text-accent-blue" aria-hidden="true" /> Step 1 &mdash; Install Claude (10 minutes)
        </h2>
        <ol className="space-y-4 text-gray-300">
          <li className="bg-gray-800/30 rounded-xl p-4">
            <p className="font-semibold text-white">1. Download the Claude desktop app</p>
            <p className="text-sm mt-1">Go to <a href="https://claude.com/download" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">claude.com/download</a> and pick your computer type. Mac users: there&rsquo;s only one Mac download &mdash; it fits every Mac. Windows users: choose the first (x64) option unless you know you have an ARM computer.</p>
          </li>
          <li className="bg-gray-800/30 rounded-xl p-4">
            <p className="font-semibold text-white">2. Open it and sign in</p>
            <p className="text-sm mt-1">Create an account (or sign in with Google) and subscribe to the <strong>Pro</strong> plan &mdash; or redeem your guest pass. That&rsquo;s the plan that includes &ldquo;Claude Code,&rdquo; the part that can work on your actual computer.</p>
          </li>
          <li className="bg-gray-800/30 rounded-xl p-4">
            <p className="font-semibold text-white">3. Click the &ldquo;Code&rdquo; tab</p>
            <p className="text-sm mt-1">The app has three tabs at the top: Chat, Cowork, and <strong>Code</strong>. Code is the one you want &mdash; ignore the programmer-sounding name; it just means &ldquo;Claude with hands.&rdquo; Click it, choose <strong>Local</strong>, and when it asks you to select a folder, make a new folder on your Desktop called <strong>Computer Fixes</strong> and pick that.</p>
          </li>
          <li className="bg-gray-800/30 rounded-xl p-4">
            <p className="font-semibold text-white">4. Windows only: one extra helper</p>
            <p className="text-sm mt-1">Windows needs a free helper program called Git before Claude can work locally. In the Code tab, just type: <em>&ldquo;I&rsquo;m not a programmer. Check if Git is installed, and if not, walk me through installing it step by step.&rdquo;</em> Claude will handle it with you.</p>
          </li>
          <li className="bg-gray-800/30 rounded-xl p-4">
            <p className="font-semibold text-white">5. Confirm you&rsquo;re in Manual mode</p>
            <p className="text-sm mt-1">You should see that Claude <strong>asks before doing anything</strong> &mdash; every action shows up with Accept and Reject buttons. That&rsquo;s Manual mode, and it&rsquo;s already on. Leave it that way forever.</p>
          </li>
        </ol>
      </section>

      {/* Step 2: first fix — the printer */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Printer className="w-6 h-6 text-accent-green" aria-hidden="true" /> Step 2 &mdash; Your first fix: the stuck printer
        </h2>
        <p className="text-gray-300 mb-4">
          The classic: the printer says &ldquo;printing&rdquo; and nothing comes out, and now there are
          nine copies of the same page jammed in the queue. Type this into Claude, word for word:
        </p>
        <div className="bg-background-secondary border border-gray-700 rounded-xl p-4 mb-4">
          <p className="text-accent-green font-mono text-sm">
            My printer says it&rsquo;s printing but nothing comes out. I think print jobs are stuck in
            the queue. Cancel the stuck jobs and restart whatever needs restarting. Explain each step
            before you do it.
          </p>
        </div>
        <p className="text-gray-300 text-sm mb-2">
          What happens next: Claude will propose one or two commands &mdash; on Windows it restarts the
          &ldquo;Print Spooler&rdquo; and clears the jammed job files; on a Mac it cancels the stuck jobs.
          Each one appears with an <strong className="text-white">Accept</strong> button. Read the
          explanation, click Accept, and try printing again.
        </p>
        <p className="text-gray-400 text-sm">
          This whole fix is straight from Microsoft&rsquo;s own support playbook &mdash; Claude is just
          the friendly hands that types it correctly.
        </p>
      </section>

      {/* Step 3: the big rescue — Office */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <PackageX className="w-6 h-6 text-accent-purple" aria-hidden="true" /> Step 3 &mdash; The big rescue: Office won&rsquo;t uninstall
        </h2>
        <p className="text-gray-300 mb-4">
          The nightmare version: Microsoft Office is broken, won&rsquo;t repair, won&rsquo;t uninstall,
          and won&rsquo;t reinstall &mdash; the computer-shop special. Microsoft actually publishes an
          official removal tool for exactly this; most people have just never heard of it. Tell Claude:
        </p>
        <div className="bg-background-secondary border border-gray-700 rounded-xl p-4 mb-4">
          <p className="text-accent-green font-mono text-sm">
            Microsoft Office is broken on this Windows computer &mdash; it won&rsquo;t uninstall or
            reinstall. Use Microsoft&rsquo;s official current removal method (the Get Help uninstall
            troubleshooter, or their command-line removal tool if needed) to fully remove Office, then
            help me reinstall it from my Microsoft account. Close my Office programs first, and explain
            every step.
          </p>
        </div>
        <ul className="space-y-2 text-gray-300 text-sm mb-4">
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 shrink-0" aria-hidden="true" /><span>Claude will close Word, Excel, and Outlook first (the tool refuses to run while they&rsquo;re open &mdash; if you see error code 06, that&rsquo;s all it means).</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 shrink-0" aria-hidden="true" /><span>It may need to run &ldquo;as administrator&rdquo; &mdash; Windows itself will show a yes/no box. That one&rsquo;s fine to approve.</span></li>
          <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-accent-green mt-0.5 shrink-0" aria-hidden="true" /><span>When it reports success, restart the computer before reinstalling. Your documents are not touched by any of this &mdash; Office removal doesn&rsquo;t delete your files.</span></li>
          <li className="flex gap-2"><XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" aria-hidden="true" /><span>Heads-up: Microsoft&rsquo;s consumer troubleshooter currently can&rsquo;t remove the &ldquo;Office 2024&rdquo; one-time-purchase version &mdash; if that&rsquo;s what you have, this is a call-us job.</span></li>
        </ul>
      </section>

      {/* More asks */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <MessageSquareText className="w-6 h-6 text-accent-blue" aria-hidden="true" /> More things to ask it
        </h2>
        <div className="space-y-4">
          {MORE_ASKS.map((item) => (
            <div key={item.title} className="bg-gray-800/30 rounded-xl p-4">
              <p className="font-semibold text-white flex items-center gap-2">
                <item.icon className="w-4 h-4 text-accent-blue" aria-hidden="true" /> {item.title}
              </p>
              <p className="text-accent-green font-mono text-sm mt-2">{item.prompt}</p>
              <p className="text-gray-400 text-sm mt-2">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The lesson — pedagogy tie-in */}
      <section className="mb-12 bg-gray-800/50 border border-gray-700 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-3">What you just learned (without noticing)</h2>
        <p className="text-gray-300 text-sm mb-3">
          That workflow &mdash; <em>let the AI propose, make it explain, then approve or reject</em> &mdash;
          is the single most important AI skill there is. We call it the <strong>Audit</strong> habit,
          and it&rsquo;s the heart of everything we teach here. You just practiced it on a printer.
        </p>
        <p className="text-gray-300 text-sm">
          Want to get genuinely good at it?{' '}
          <Link to="/shift" className="text-accent-blue hover:underline">Take the free 3-minute challenge</Link>{' '}
          or browse the <Link to="/#tracks" className="text-accent-blue hover:underline">free lessons</Link>.
        </p>
      </section>

      {/* Human fallback */}
      <section className="text-center bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-gray-700 rounded-xl p-8">
        <PhoneCall className="w-8 h-8 text-accent-blue mx-auto mb-3" aria-hidden="true" />
        <h2 className="text-xl font-bold text-white mb-2">Rather have a human do it?</h2>
        <p className="text-gray-300 text-sm mb-4">
          We&rsquo;re a father-and-son team in Cleveland. If your computer is older than these tools
          support, or you&rsquo;d just rather hand it to someone &mdash; call or text us. No judgment;
          half our own family calls us instead of the AI too.
        </p>
        <a href="tel:+12169995765" className="inline-block bg-accent-blue text-slate-900 font-medium px-6 py-3 rounded-lg hover:bg-blue-500 transition-colors">
          Call or text (216) 999-5765
        </a>
      </section>
    </div>
  );
}
